/* eslint-disable no-nested-ternary */
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment } from 'react';
import { Navigate } from 'react-router-dom';

import {
  AtivoStatus,
  RequiredFile,
} from '../../../../../../api/theHive/redeProto/types';
import {
  IconExpandMoreMS,
  IconKeyboardArrowRightMS,
} from '../../../../../../constants/icons';
import { formatCnpjCpf, formatDate } from '../../../../../../helpers';
import { StatusIcon } from './StatusIcon';
import useInAccreditation from './useInAccreditation';

const ATIVO_LABEL: Record<AtivoStatus, string> = {
  trial: 'Trial',
  ativo: 'Ativo',
  inativo: 'Inativo',
};

const ATIVO_COLOR: Record<AtivoStatus, 'info' | 'success' | 'default'> = {
  trial: 'info',
  ativo: 'success',
  inativo: 'default',
};

// Colapsado: % de documentos aprovados sobre o total exigido do eixo.
function AxisPercent({ files }: { files: RequiredFile[] }): JSX.Element {
  if (files.length === 0) {
    return <Typography color="text.secondary">—</Typography>;
  }
  const approved = files.filter((f) => f.status === 'approved').length;
  const percent = Math.round((approved / files.length) * 100);
  return (
    <Tooltip title={`${approved} de ${files.length} documentos aprovados`}>
      <Typography fontWeight={600}>{percent}%</Typography>
    </Tooltip>
  );
}

function AxisFiles({ files }: { files: RequiredFile[] }): JSX.Element {
  if (files.length === 0) {
    return (
      <Typography color="text.secondary" fontSize={13}>
        Sem documentos neste eixo.
      </Typography>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {files.map((file) => (
        <Box key={file.fileName} display="flex" alignItems="center" gap={1}>
          <StatusIcon status={file.status} />
          <Typography fontSize={13}>{file.fileName}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function InAccreditation(): JSX.Element {
  const {
    role,
    isAllowed,
    items,
    page,
    setPage,
    totalPages,
    loading,
    expanded,
    toggleExpand,
    order,
    setOrder,
  } = useInAccreditation();

  // RBAC: só admin (plataforma) e admin do banco. Demais caem na Visão Geral.
  if (role !== null && !isAllowed) {
    return <Navigate to="visao-geral" replace />;
  }

  return (
    <Box mt={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Typography color="text.primary" fontSize={20} fontWeight={600}>
          Em Credenciamento
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={order}
          onChange={(_, next) => next && setOrder(next)}
        >
          <ToggleButton value="fifo" sx={{ textTransform: 'none' }}>
            Mais antigos
          </ToggleButton>
          <ToggleButton value="recent" sx={{ textTransform: 'none' }}>
            Mais recentes
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="4%" />
              <TableCell>Nome da empresa</TableCell>
              <TableCell align="center">CNPJ</TableCell>
              <TableCell align="center">Data do pedido</TableCell>
              <TableCell align="center">Edital</TableCell>
              <TableCell align="center">Jurídico</TableCell>
              <TableCell align="center">Engenharia</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Pagante</TableCell>
              <TableCell align="center">Abandonou</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Nenhuma empresa em credenciamento.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isOpen = expanded.includes(item.noticeCompanyId);
                return (
                  <Fragment key={item.noticeCompanyId}>
                    <TableRow
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleExpand(item.noticeCompanyId)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {isOpen ? IconExpandMoreMS : IconKeyboardArrowRightMS}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.companyName}</TableCell>
                      <TableCell align="center">
                        {formatCnpjCpf(item.cnpj)}
                      </TableCell>
                      <TableCell align="center">
                        {item.requestedAt
                          ? formatDate(item.requestedAt.split('T')[0])
                          : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {item.noticeName ?? '—'}
                      </TableCell>
                      <TableCell align="center">
                        <AxisPercent files={item.juridico} />
                      </TableCell>
                      <TableCell align="center">
                        <AxisPercent files={item.engenharia} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={ATIVO_COLOR[item.ativo]}
                          label={ATIVO_LABEL[item.ativo]}
                        />
                      </TableCell>
                      <TableCell align="center">{item.plano ?? '—'}</TableCell>
                      <TableCell align="center">
                        {item.abandoned ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label="Sim"
                          />
                        ) : (
                          <Typography color="text.secondary">Não</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit timeout="auto">
                          <Box
                            display="flex"
                            gap={6}
                            py={2}
                            px={4}
                            flexWrap="wrap"
                          >
                            <Box minWidth={260}>
                              <Typography fontWeight={600} mb={1}>
                                Jurídico
                              </Typography>
                              <AxisFiles files={item.juridico} />
                            </Box>
                            <Box minWidth={260}>
                              <Typography fontWeight={600} mb={1}>
                                Engenharia
                              </Typography>
                              <AxisFiles files={item.engenharia} />
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      )}
    </Box>
  );
}
